import { z } from "zod";

const proofClaimsSchema = z.object({
  aud: z.literal("blastro-installation-link"),
  appClientId: z.string().min(1),
  shop: z.string().regex(/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/),
  siteId: z.string().min(1),
  installationEpoch: z.string().regex(/^[1-9]\d*$/),
  exp: z.number().int().positive(),
  nonce: z.string().regex(/^[A-Za-z0-9_-]{22}$/),
});

export const blastroConnectPath = (siteId: string) =>
  `/blastro/${encodeURIComponent(siteId)}/connect`;

let captured: { pathname: string; proof: string } | null = null;

// Strip the bearer-like proof before Sentry or login redirects can record it.
// Keep it only in memory so an SPA login can return to the confirmation page.
export function captureBlastroLinkFragment(): void {
  if (
    typeof window === "undefined" ||
    !/^\/blastro\/[^/]+\/connect\/?$/.test(window.location.pathname) ||
    !window.location.hash
  )
    return;

  const params = new URLSearchParams(window.location.hash.slice(1));
  const proof = params.get("proof") || "";
  captured = {
    pathname: window.location.pathname.replace(/\/$/, ""),
    proof: params.size === 1 && proof.length <= 8192 ? proof : "",
  };
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${window.location.search}`,
  );
}

export function readBlastroLinkProof(siteId: string): string | null {
  captureBlastroLinkFragment();
  return captured?.pathname === blastroConnectPath(siteId)
    ? captured.proof
    : null;
}

export function forgetBlastroLinkProof(): void {
  captured = null;
}

export function getConfiguredBlastroConnectUrl(): URL | null {
  try {
    const url = new URL(
      import.meta.env.VITE_BLASTRO_SHOPIFY_CONNECT_URL as string,
    );
    return url.protocol === "https:" &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash &&
      url.pathname === "/shopify/app/connect"
      ? url
      : null;
  } catch {
    return null;
  }
}

// Rollout is opt-in per brand, with no global or role-based bypass.
export function isBlastroEnabledForSite(siteId?: string): boolean {
  if (!siteId || !getConfiguredBlastroConnectUrl()) return false;
  const pilotSiteIds = (import.meta.env.VITE_BLASTRO_PILOT_SITE_IDS || "")
    .split(",")
    .map((id: string) => id.trim())
    .filter(Boolean);
  return pilotSiteIds.includes(siteId);
}

// These claims are display hints only. Bridge verifies the signature, current
// installation, one-time nonce and Queen One brand membership before linking.
export function readBlastroLinkClaims(
  proof: string,
  siteId: string,
  now = Date.now(),
) {
  if (proof.length > 8192) return null;
  const [payload, signature, extra] = proof.split(".");
  if (
    !payload ||
    !/^[A-Za-z0-9_-]+$/.test(payload) ||
    !/^[A-Za-z0-9_-]{43}$/.test(signature || "") ||
    extra !== undefined
  )
    return null;

  try {
    const decoded: unknown = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    );
    const claims = proofClaimsSchema.parse(decoded);
    const nowSeconds = Math.floor(now / 1000);
    return claims.siteId === siteId && claims.exp > nowSeconds ? claims : null;
  } catch {
    return null;
  }
}
